import { useRef } from 'react';
import { Box, Container, Typography, Button, Chip } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '../../icons';
import { landingColors, landingFont, eyebrowSx, sectionHeadingSx } from '../landingTheme';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { PRICING_PLANS } from '../landingContent';

export default function PricingSection({ onNavigate }) {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);

  return (
    <Box
      ref={sectionRef}
      component="section"
      id="pricing-section"
      aria-label="Pricing"
      sx={{ py: { xs: 9, md: 15 }, backgroundColor: landingColors.night, color: '#fff' }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 4 } }}>
        <Box sx={{ textAlign: 'center', maxWidth: 700, mx: 'auto', mb: { xs: 5, md: 8 } }}>
          <Typography data-reveal sx={{ ...eyebrowSx, color: landingColors.indigo, fontFamily: landingFont, mb: 1.5 }}>
            Pricing
          </Typography>
          <Typography data-reveal data-reveal-delay="0.06" component="h2" sx={{ ...sectionHeadingSx, fontFamily: landingFont, mb: 2 }}>
            Simple, transparent pricing
          </Typography>
          <Typography data-reveal data-reveal-delay="0.12" sx={{ fontFamily: landingFont, color: landingColors.onNightMuted, fontSize: { xs: '1rem', md: '1.2rem' }, lineHeight: 1.6 }}>
            Start your 14-day free trial today. No credit card required.
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, maxWidth: 880, mx: 'auto', alignItems: 'stretch' }}>
          {PRICING_PLANS.map((plan, index) => (
            <Box
              key={plan.name}
              data-reveal
              data-reveal-delay={(index * 0.1).toString()}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '24px',
                p: { xs: 3.5, md: 4 },
                backgroundColor: plan.featured ? landingColors.indigo : landingColors.nightPanel,
                border: plan.featured
                  ? `1px solid ${landingColors.indigo}`
                  : `1px solid ${landingColors.nightLine}`,
                boxShadow: plan.featured ? '0 30px 70px rgba(102,126,234,0.35)' : 'none',
              }}
            >
              <Chip
                label={plan.badge}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  fontFamily: landingFont,
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  fontSize: '0.7rem',
                  mb: 2,
                  color: plan.featured ? landingColors.indigo : '#fff',
                  backgroundColor: plan.featured ? '#fff' : 'rgba(255,255,255,0.12)',
                }}
              />
              <Typography sx={{ fontFamily: landingFont, fontWeight: 700, fontSize: '1.2rem', mb: 1 }}>
                {plan.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5 }}>
                <Typography sx={{ fontFamily: landingFont, fontWeight: 800, fontSize: { xs: '2.6rem', md: '3rem' }, lineHeight: 1 }}>
                  {plan.price}
                </Typography>
                <Typography sx={{ fontFamily: landingFont, opacity: 0.8, fontSize: '1rem' }}>{plan.period}</Typography>
              </Box>
              <Typography sx={{ fontFamily: landingFont, opacity: 0.82, fontSize: '0.9rem', mb: 3 }}>
                {plan.blurb}
              </Typography>

              <Box sx={{ flex: 1, mb: 3 }}>
                {plan.features.map((feature) => (
                  <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                    <CheckCircleIcon sx={{ fontSize: 20, color: plan.featured ? '#fff' : landingColors.indigo }} />
                    <Typography sx={{ fontFamily: landingFont, fontSize: '0.92rem', opacity: 0.95 }}>{feature}</Typography>
                  </Box>
                ))}
              </Box>

              <Button
                fullWidth
                disableElevation
                variant={plan.featured ? 'contained' : 'outlined'}
                onClick={() => onNavigate('/register')}
                sx={{
                  fontFamily: landingFont,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '1rem',
                  py: 1.5,
                  borderRadius: '14px',
                  ...(plan.featured
                    ? {
                        backgroundColor: '#fff',
                        color: landingColors.indigo,
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                      }
                    : {
                        borderColor: landingColors.indigo,
                        color: '#fff',
                        '&:hover': { backgroundColor: landingColors.indigo, borderColor: landingColors.indigo },
                      }),
                }}
              >
                {plan.cta}
              </Button>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
